'use client';
import { useState, useEffect, useRef } from 'react';
import { ArrowRight } from 'lucide-react';
import Image from 'next/image';
import LinkNoPrefetch from '@/components/link-noprefetch';
import { DotBlockEditor } from '../shared/decodeTextBlock';
import { SvgIcon } from '../shared/SvgIcon';
import { cn } from '@/app/utils/cn';

// Helper to syntax highlight code strings
function highlightCode(codeString) {
    const keywords = [
        'import',
        'from',
        'const',
        'let',
        'var',
        'await',
        'async',
        'function',
        'return',
        'export',
        'default'
    ];

    // Regex patterns for different token types (order matters!)
    const patterns = [
        { type: 'comment', regex: /\/\/.*$/gm, className: 'text-grey' },
        {
            type: 'string',
            regex: /(['"`])(?:(?=(\\?))\2.)*?\1/g,
            className: 'text-green-600'
        },
        {
            type: 'keyword',
            regex: new RegExp(`\\b(${keywords.join('|')})\\b`, 'g'),
            className: 'text-fuschia-600'
        },
        {
            type: 'property-method',
            regex: /\.[a-zA-Z_$][a-zA-Z0-9_$]*(?=\s*\()/g,
            className: 'text-yellow-600'
        },
        {
            type: 'method',
            regex: /\b[a-zA-Z_$][a-zA-Z0-9_$]*(?=\s*\()/g,
            className: 'text-yellow-600'
        },
        {
            type: 'property',
            regex: /\.[a-zA-Z_$][a-zA-Z0-9_$]*(?!\s*\()/g,
            className: 'text-blue-600'
        },
        { type: 'number', regex: /\b\d+\.?\d*\b/g, className: 'text-orange-600' }
    ];

    // Create an array of all matches with their positions
    const matches = [];
    patterns.forEach(({ type, regex, className }) => {
        let match;
        const r = new RegExp(regex);
        while ((match = r.exec(codeString)) !== null) {
            matches.push({
                type,
                className,
                start: match.index,
                end: match.index + match[0].length,
                text: match[0]
            });
        }
    });

    // Sort matches by start position, then by length (longer matches first)
    matches.sort((a, b) => {
        if (a.start !== b.start) return a.start - b.start;
        return b.end - b.start - (a.end - a.start);
    });

    // Remove overlapping matches (keep first match at each position)
    const filteredMatches = [];
    let lastEnd = 0;
    matches.forEach((match) => {
        if (match.start >= lastEnd) {
            filteredMatches.push(match);
            lastEnd = match.end;
        }
    });

    // Build the highlighted output
    const result = [];
    let position = 0;

    filteredMatches.forEach((match, idx) => {
        // Add any text before this match as plain text
        if (position < match.start) {
            result.push(
                <span key={`text-${idx}`} className="text-white">
                    {codeString.substring(position, match.start)}
                </span>
            );
        }

        // Add the highlighted match
        result.push(
            <span key={`match-${idx}`} className={match.className}>
                {match.text}
            </span>
        );

        position = match.end;
    });

    // Add any remaining text
    if (position < codeString.length) {
        result.push(
            <span key="text-end" className="text-white">
                {codeString.substring(position)}
            </span>
        );
    }

    return result;
}

// Helper to normalize feature data from DotCMS
function normalizeFeaturesToRender(features) {
    return (
        features?.map((feature, index) => {
            return {
                id: feature.identifier || `feature-${index}`,
                title: feature.title || '',
                headline: feature.userTypeTitle || '',
                description: feature.userTypeDescription || '',
                icon: feature.userTypeIcon?.fileAsset?.idPath,
                image: feature.userTypeImage?.fileAsset?.idPath,
                overlayType: feature.overlayType || null,
                overlayContent: feature.overlayContent || null,
                callToAction: feature.featureCta || [],
                features: feature.featuresForUserType || []
            };
        }) || []
    );
}

/**
 * PersonaSection Layout01
 *
 * Expects DotCMS data structure:
 * - features: Array where first item contains headline and description for the section header
 * - All features: { title, headline, description, icon, image, overlayType, callToAction[], features[] }
 * - overlayType: 'code' | 'workflow' | 'stats' | null (custom overlayContent)
 */
function Layout01({ features: rawFeatures = [] }) {
    // Normalize DotCMS data structure
    const features = normalizeFeaturesToRender(rawFeatures);
    const [activeFeature, setActiveFeature] = useState(0);
    const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });
    const buttonRefs = useRef([]);

    useEffect(() => {
        const updateIndicator = () => {
            const activeButton = buttonRefs.current[activeFeature];
            if (activeButton) {
                setIndicatorStyle({
                    left: activeButton.offsetLeft,
                    width: activeButton.offsetWidth
                });
            }
        };

        const timeoutId = setTimeout(updateIndicator, 0);

        window.addEventListener('resize', updateIndicator);
        return () => {
            clearTimeout(timeoutId);
            window.removeEventListener('resize', updateIndicator);
        };
    }, [activeFeature]);

    if (!features || features.length === 0) return null;

    return (
        <section className={cn('overflow-hidden py-20 text-white lg:py-28')}>
            <div className="container mx-auto px-1">
                {/* Persona switcher */}
                <div className="mb-12 flex justify-center">
                    <div className="relative inline-flex rounded-xl border border-white/10 bg-white/10 p-1.5">
                        {/* Animated indicator */}
                        <div
                            className="absolute rounded-lg bg-fuschia-600 shadow-lg transition-all duration-300 ease-in-out"
                            style={{
                                left: `${indicatorStyle.left}px`,
                                width: `${indicatorStyle.width}px`,
                                top: '6px',
                                bottom: '6px'
                            }}
                        />

                        {features.map((p, idx) => {
                            const isActive = activeFeature === idx;
                            return (
                                <button
                                    key={p.identifier || idx}
                                    ref={(el) => (buttonRefs.current[idx] = el)}
                                    onClick={() => setActiveFeature(idx)}
                                    className={cn(
                                        'relative z-10 flex flex-col lg:flex-row items-center justify-center gap-2 rounded-lg px-6 py-3 text-xs md:text-sm font-medium transition-colors duration-200',
                                        isActive && 'text-white',
                                        !isActive && 'text-white/70 hover:text-white'
                                    )}>
                                    {p.icon && <SvgIcon icon={p.icon} size={20} />}
                                    {p.title}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="flex flex-col-reverse gap-12 md:grid md:grid-cols-2 md:items-center md:gap-16">
                    {/* Content side */}
                    <div className="relative min-h-[500px] w-full">
                        {features.map((p, idx) => (
                            <div
                                key={p.id ?? p.identifier ?? idx}
                                className={cn(
                                    'transition-all duration-500',
                                    activeFeature === idx
                                        ? 'relative opacity-100 translate-x-0'
                                        : 'absolute inset-0 opacity-0 translate-x-4 pointer-events-none'
                                )}>
                                {p.headline && (
                                    <h2 className="mb-6 text-3xl font-bold leading-tight lg:text-5xl">
                                        {typeof p.headline === 'string' ? (
                                            <span>{p.headline}</span>
                                        ) : (
                                            <DotBlockEditor blocks={p.headline} />
                                        )}
                                    </h2>
                                )}

                                {p.description && (
                                    <div className="text-lg mb-10 max-w-lg text-white/70">
                                        {typeof p.description === 'string' ? (
                                            <p>{p.description}</p>
                                        ) : (
                                            <DotBlockEditor blocks={p.description} />
                                        )}
                                    </div>
                                )}

                                {/* Feature grid */}
                                {p.features && p.features.length > 0 && (
                                    <div className="mb-10 grid grid-cols-2 gap-4">
                                        {p.features.map((feature, featureIdx) => (
                                            <div
                                                key={feature.identifier || featureIdx}
                                                className="flex flex-col gap-4 rounded-xl border border-white/10 bg-white/5 p-4 transition-colors hover:bg-white/10"
                                                style={{
                                                    transitionDelay:
                                                        activeFeature === idx
                                                            ? `${featureIdx * 50}ms`
                                                            : '0ms'
                                                }}>
                                                {feature.icon?.fileAsset?.idPath && (
                                                    <SvgIcon
                                                        icon={feature.icon.fileAsset.idPath}
                                                        className="text-fuschia-600"
                                                        size={24}
                                                    />
                                                )}
                                                <div>
                                                    {feature.title && (
                                                        <h3 className="text-sm font-semibold">
                                                            {feature.title}
                                                        </h3>
                                                    )}
                                                    {feature.description && (
                                                        <div className="text-xs text-white/60">
                                                            {typeof feature.description ===
                                                            'string' ? (
                                                                <p>{feature.description}</p>
                                                            ) : (
                                                                <DotBlockEditor
                                                                    blocks={feature.description}
                                                                />
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* CTAs */}
                                <div className="flex flex-wrap gap-4">
                                    {p.callToAction?.[0] && (
                                        <LinkNoPrefetch
                                            href={p.callToAction[0].url}
                                            className="group inline-flex items-center gap-2 rounded-lg bg-fuschia-600 px-6 py-3 font-semibold text-white transition-colors hover:bg-fuschia-700">
                                            {p.callToAction[0].urlName || 'Learn More'}
                                            <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                                        </LinkNoPrefetch>
                                    )}
                                    {p.callToAction?.[1] && (
                                        <LinkNoPrefetch
                                            href={p.callToAction[1].url}
                                            className="inline-flex items-center gap-2 rounded-lg border border-white/20 px-6 py-3 font-semibold text-white transition-colors hover:bg-white/10">
                                            {p.callToAction[1].urlName || 'Learn More'}
                                        </LinkNoPrefetch>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Visual side */}
                    <div className="relative w-full">
                        {/* Decorative glow */}
                        <div className="-inset-4 hidden rounded-3xl bg-gradient-to-r from-fuschia-600/20 to-fuschia-600/5 blur-3xl md:absolute" />

                        <div className="relative overflow-hidden rounded-2xl border border-white/10 shadow-2xl">
                            {/* Images - stacked absolutely */}
                            <div className="aspect-[3/3.2] bg-white/5 lg:aspect-[4/3]">
                                {features.map((p, idx) => {
                                    const isActive = activeFeature === idx;

                                    return (
                                        <div
                                            key={`image-${p.id}`}
                                            className={cn(
                                                'absolute inset-0 transition-all duration-700',
                                                isActive
                                                    ? 'opacity-100 scale-100'
                                                    : 'opacity-0 scale-105'
                                            )}>
                                            {p.image ? (
                                                <Image
                                                    src={p.image}
                                                    alt={p.title || 'Persona'}
                                                    width={800}
                                                    height={600}
                                                    className="size-full object-cover"
                                                />
                                            ) : (
                                                <div className="flex size-full items-center justify-center">
                                                    <div className="p-8 text-center">
                                                        <div className="mx-auto mb-4 flex size-16 items-center justify-center rounded-2xl bg-white/10">
                                                            <svg
                                                                className="size-8 text-white/40"
                                                                viewBox="0 0 24 24"
                                                                fill="none"
                                                                stroke="currentColor">
                                                                <circle cx="12" cy="12" r="10" />
                                                            </svg>
                                                        </div>
                                                        <p className="font-medium text-white/40">
                                                            {p.title} Image
                                                        </p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Overlay content - Different for each persona */}
                            {features.map((p, idx) => {
                                const isActive = activeFeature === idx;

                                return (
                                    <div
                                        key={`overlay-${p.id}`}
                                        className={cn(
                                            'absolute inset-x-0 bottom-0 p-1.5 lg:p-6 transition-all duration-700',
                                            'bg-gradient-to-t from-blue-700 via-blue-700/90 to-transparent',
                                            isActive ? 'opacity-100' : 'opacity-0'
                                        )}>
                                        {/* Custom overlay content based on overlayType or default to overlayContent */}
                                        {p.overlayType === 'code' && (
                                            <div className="rounded-lg border border-white/10 bg-blue-700/80 p-4 font-mono text-xs backdrop-blur-sm">
                                                <div className="mb-3 text-xs text-white/50">
                                                    {p.overlayContent.title}
                                                </div>
                                                {p.overlayContent.code
                                                    ?.split('\n')
                                                    .map((line, lineIdx) => (
                                                        <div
                                                            key={lineIdx}
                                                            className={cn(
                                                                'font-medium leading-relaxed tracking-wide text-xs lg:text-sm',
                                                                lineIdx > 0 && 'mt-2'
                                                            )}>
                                                            {highlightCode(line)}
                                                        </div>
                                                    ))}
                                            </div>
                                        )}

                                        {p.overlayType === 'workflow' && (
                                            <div className="rounded-lg border border-white/10 bg-blue-700/80 p-4 backdrop-blur-sm">
                                                <div className="mb-3 text-xs text-white/50">
                                                    {p.overlayContent.title}
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {p.overlayContent.content.map(
                                                        (item, stepIdx) => (
                                                            <div
                                                                key={stepIdx}
                                                                className="flex flex-1 flex-col items-center">
                                                                <div
                                                                    className={cn(
                                                                        'mb-1 flex size-8 items-center justify-center rounded-full text-xs font-medium',
                                                                        item.status ===
                                                                            'complete' &&
                                                                            'border border-green-500/30 bg-green-500/20 text-green-400',
                                                                        item.status === 'active' &&
                                                                            'animate-pulse border border-fuschia-600/30 bg-fuschia-600/20 text-fuschia-400',
                                                                        item.status === 'pending' &&
                                                                            'border border-white/20 bg-white/10 text-white/40'
                                                                    )}>
                                                                    {stepIdx + 1}
                                                                </div>
                                                                <span className="text-[10px] text-white/60">
                                                                    {item.step}
                                                                </span>
                                                                <span className="text-[10px] text-white/40">
                                                                    {item.user}
                                                                </span>
                                                            </div>
                                                        )
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {p.overlayType === 'stats' && (
                                            <div className="rounded-lg border border-white/10 bg-blue-700/80 p-4 backdrop-blur-sm">
                                                <div className="mb-3 text-xs text-white/50">
                                                    {p.overlayContent.title}
                                                </div>
                                                <div className="flex justify-around gap-3">
                                                    {p.overlayContent.content.map(
                                                        (metric, metricIdx) => (
                                                            <div
                                                                key={metricIdx}
                                                                className="text-center">
                                                                <div className="text-lg font-bold text-fuschia-600">
                                                                    {metric.value}
                                                                </div>
                                                                <div className="text-[11px] text-white/60">
                                                                    {metric.label}
                                                                </div>
                                                            </div>
                                                        )
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* Fallback to custom overlayContent from CMS */}
                                        {!p.overlayType && p.overlayContent && (
                                            <div className="rounded-lg border border-white/10 bg-blue-700/80 p-4 backdrop-blur-sm">
                                                <DotBlockEditor blocks={p.overlayContent} />
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

const layoutComponents = {
    1: Layout01
};

function PersonaSection(props) {
    const { layout = 1 } = props;
    const LayoutComponent = layoutComponents[layout] || Layout01;

    return <LayoutComponent {...props} />;
}

export default PersonaSection;
